import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  channelMemberApiClient,
  ChannelMemberErrorResponse,
  ChannelMemberInviteRequest,
  ChannelMemberSuggestRequest,
  ChannelMemberSuggestResponse,
} from '@src/api/shared/community/channelMemberApiClient';
import DefaultIcon from '@src/assets/images/avatar/default.png';
import TrashIcon from '@src/assets/images/common/icon/channel/trash.png';
import { useUnsavedInviteUsers } from '@src/context/UnsavedInviteUsersContext';
import { ConfirmMemberInvitationLeaveModal } from '@src/features/shared/community/components/ConfirmMemberInvitationLeaveModal';
import { useAsyncHandler } from '@src/hooks/useAsyncHandler';
import { useUnsavedChanges } from '@src/hooks/useUnsavedChange.tsx';
import { SHARED_URL } from '@src/routes/shared';
import Button from '@src/ui/elements/button/Button';
import { ErrorMessage } from '@src/ui/elements/form';
import { firstError } from '@src/utils/errorMessageUtils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

type FormValues = {
  email: string;
};

function CommunityMemberInvitationForm() {
  const { setUnsavedInviteUsers } = useUnsavedInviteUsers();
  const navigate = useNavigate();
  const { code } = useParams<{ code: string }>();
  const [formValues, setFormValues] = useState<FormValues | null>(() => ({
    email: '',
  }));
  const [keyword, setKeyword] = useState<string>('');
  const [errors, setErrors] = useState<ChannelMemberErrorResponse>();
  const [suggestedUsers, setSuggestedUsers] = useState<ChannelMemberSuggestResponse | undefined>();
  const [inviteUsers, setInviteUsers] = useState<ChannelMemberSuggestResponse>([]);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  // 複数選択用の選択状態管理
  const [selectedUsers, setSelectedUsers] = useState<ChannelMemberSuggestResponse>([]);
  const { isModalOpen, confirmReturn, cancelReturn } = useUnsavedChanges({
    hasUnsavedChanges: inviteUsers.length > 0,
    onConfirm: () => {
      setInviteUsers([]);
      navigate(generatePath(SHARED_URL.COMMUNITY_CHANNEL_MEMBERS, { code }));
    },
  });

  const KEYUP_WAIT_TIME = 500;

  // ユーザーの選択状態を切り替え（検索ボックスのタグに追加/削除）
  const toggleUserSelection = (user: ChannelMemberSuggestResponse[0]) => {
    const existingIndex = selectedUsers.findIndex(
      (selectedUser) => selectedUser.id === user.id && selectedUser.user_type === user.user_type,
    );

    setSelectedUsers(
      existingIndex >= 0
        ? selectedUsers.filter((_, index) => index !== existingIndex)
        : [...selectedUsers, user],
    );

    // 最後の1人を選択した時に入力値をクリア
    if (filteredSuggestedUsers && filteredSuggestedUsers.length === 1 && existingIndex === -1) {
      setKeyword('');
      setInputValue('');
    }
  };

  // 選択状態のユーザーを招待リストに一括追加
  const addSelectedUsersToInviteList = useCallback(() => {
    if (selectedUsers.length > 0) {
      setInviteUsers([...inviteUsers, ...selectedUsers]);
      setSelectedUsers([]);
      setKeyword('');
      setFormValues({ ...formValues, email: '' });
      setSuggestedUsers(undefined);
      setSelectedIndex(null);
      setInputValue('');
    }
  }, [selectedUsers, inviteUsers, formValues]);

  // 検索ボックス内のタグからユーザーを削除
  const removeSelectedUser = (userId: string, userType: string) => {
    setSelectedUsers(
      selectedUsers.filter(
        (user) => !(user.id.toString() === userId && user.user_type === userType),
      ),
    );
    inputRef.current?.focus();
  };

  const handleMemberRemoveClick = (index: number) => {
    const removeUsers = inviteUsers.filter((_, i) => i !== index);
    setInviteUsers(removeUsers);
  };

  const { loading: isLoadingInvitationClick, execute: handleInvitationClick } = useAsyncHandler(
    async () => {
      if (!code) {
        return;
      }
      setErrors(undefined);

      const params: ChannelMemberInviteRequest = {
        channel_members: inviteUsers.map((user) => ({
          user_id: user.id,
          user_type: user.user_type,
        })),
      };
      const res = await channelMemberApiClient.invite(params, code);
      handleApiResponse(
        res,
        () => {
          setUnsavedInviteUsers([]);
          navigate(generatePath(SHARED_URL.COMMUNITY_CHANNEL_MEMBERS, { code }));
        },
        (errors) => {
          setErrors(errors);
        },
      );
    },
  );

  const handleCancelClick = () => {
    if (inviteUsers.length > 0) {
      setIsLeaveModalOpen(true);
    } else {
      setUnsavedInviteUsers([]);
      navigate(generatePath(SHARED_URL.COMMUNITY_CHANNEL_MEMBERS, { code }));
    }
  };

  const onConfirm = () => {
    setUnsavedInviteUsers([]);
    navigate(generatePath(SHARED_URL.COMMUNITY_CHANNEL_MEMBERS, { code }));
  };

  const onCancel = () => {
    setIsLeaveModalOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSuggestedUsers(undefined);
    setInputValue(e.target.value);
  };

  const handleInputFocus = () => {
    if (inputValue.trim() && !suggestedUsers) {
      getSuggestedUsers(inputValue);
    }
  };

  const getSuggestedUsers = async (keyword: string) => {
    if (!code || !keyword.trim()) {
      return;
    }
    const params: ChannelMemberSuggestRequest = {
      keyword: keyword,
    };
    const res = await channelMemberApiClient.suggest(code, params);
    handleApiResponse(res, (data) => {
      setSuggestedUsers(data);
    });
  };

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setFormValues({
        ...formValues,
        email: inputValue,
      });
      setKeyword(inputValue);
      getSuggestedUsers(inputValue);
    }, KEYUP_WAIT_TIME);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  // リアルタイムでフィルタリングされたユーザーリスト
  const filteredSuggestedUsers = suggestedUsers?.filter((user) => {
    const isInInviteList = inviteUsers?.some(
      (inviteUser) => inviteUser.id === user.id && inviteUser.user_type === user.user_type,
    );
    const isSelected = selectedUsers.some(
      (selectedUser) => selectedUser.id === user.id && selectedUser.user_type === user.user_type,
    );
    return !isInInviteList && !isSelected;
  });

  // キーボード操作: 矢印キーで選択、Enterで選択状態切り替え、Tabで一括追加
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!filteredSuggestedUsers || filteredSuggestedUsers.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prevIndex) =>
          prevIndex === null || prevIndex === filteredSuggestedUsers.length - 1 ? 0 : prevIndex + 1,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prevIndex) =>
          prevIndex === null || prevIndex === 0 ? filteredSuggestedUsers.length - 1 : prevIndex - 1,
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex !== null && filteredSuggestedUsers[selectedIndex]) {
          toggleUserSelection(filteredSuggestedUsers[selectedIndex]);
        }
        break;
      case 'Tab':
        e.preventDefault();
        addSelectedUsersToInviteList();
        break;
      case 'Escape':
        e.preventDefault();
        setSuggestedUsers(undefined);
        setSelectedIndex(null);
        setSelectedUsers([]);
        break;
    }
  };

  useEffect(() => {
    if (dropdownRef.current && selectedIndex !== null) {
      const selectedItem = dropdownRef.current.children[selectedIndex] as HTMLElement;
      selectedItem?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  useEffect(() => {
    setUnsavedInviteUsers(inviteUsers.length > 0 ? inviteUsers : []);
  }, [inviteUsers, setUnsavedInviteUsers]);

  // プルダウン外部クリックでプルダウンを閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current || containerRef.current.contains(event.target as Node)) {
        return;
      }

      setSuggestedUsers(undefined);
      setSelectedIndex(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <main className='px-6 py-[60px] md:px-4 md:pb-8 md:pt-[38px]'>
      <h1 className='text-xl font-semibold'>チャンネルに招待</h1>
      <div className='px-[116px] pt-[44px] lg:px-0 lg:pt-9'>
        <div className='mb-4 flex h-8 items-center justify-between lg:mb-6'>
          <p className='font-medium'>参加者選択</p>
          <div className='h-8'>
            {selectedUsers.length > 0 && (
              <Button
                size='s'
                variant='primary'
                onClick={addSelectedUsersToInviteList}
              >
                追加 ({selectedUsers.length})
              </Button>
            )}
          </div>
        </div>
        <div>
          <div
            className='relative'
            ref={containerRef}
          >
            <div
              className={`${
                errors ? 'border-warning-500 bg-warning-200/70' : 'border-gray-200'
              } min-h-[48px] w-full rounded-sm border border-solid px-3 py-2 font-semibold outline-none`}
            >
              <div className='flex flex-wrap items-center gap-2'>
                {selectedUsers.map((user) => (
                  <div
                    key={`${user.id}-${user.user_type}`}
                    className='flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-sm text-white'
                  >
                    <img
                      width={16}
                      height={16}
                      src={user.avatar_url || DefaultIcon}
                      alt=''
                      className='size-4 rounded-full object-contain'
                    />
                    <span className='max-w-[120px] truncate'>{user.display_name}</span>
                    <button
                      type='button'
                      onClick={() => removeSelectedUser(user.id.toString(), user.user_type)}
                      className='ml-1 flex items-center justify-center'
                    >
                      <span className='text-white hover:text-gray-200'>×</span>
                    </button>
                  </div>
                ))}
                <input
                  ref={inputRef}
                  type='text'
                  className='min-w-[120px] flex-1 bg-transparent py-2 outline-none placeholder:text-gray-200'
                  placeholder={selectedUsers.length > 0 ? '' : 'キーワードを入力'}
                  value={inputValue}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>
            {errors?.channel_members?.map((error, index) => (
              <div key={index}>
                {error.user_id && (
                  <ErrorMessage>
                    {`${inviteUsers[index]?.email}: ${firstError(error.user_id)}`}
                  </ErrorMessage>
                )}
                {error.user_type && (
                  <ErrorMessage>
                    {`${inviteUsers[index]?.email}: ${firstError(error.user_type)}`}
                  </ErrorMessage>
                )}
              </div>
            ))}
            {keyword && filteredSuggestedUsers && filteredSuggestedUsers.length > 0 && (
              <div
                ref={dropdownRef}
                className='absolute left-0 top-[calc(100%+10px)] z-50 max-h-[370px] w-full overflow-y-auto rounded-[4px] border border-solid border-gray-200 bg-white lg:top-[calc(100%+6px)]'
              >
                {filteredSuggestedUsers.map((user, index) => (
                  <button
                    key={index}
                    type='button'
                    className={`flex w-full hover:bg-primary hover:text-white ${
                      selectedIndex === index ? 'bg-primary text-white' : ''
                    }`}
                    onClick={() => {
                      toggleUserSelection(user);
                      // クリック後もキーボード操作を継続可能に
                      inputRef.current?.focus();
                    }}
                  >
                    <div className='flex min-w-0 items-center px-4 py-1 lg:w-full'>
                      <div className='shrink-0'>
                        <img
                          width={28}
                          height={28}
                          src={user.avatar_url || DefaultIcon}
                          alt=''
                          className='size-7 rounded-full object-contain'
                        />
                      </div>
                      <p className='truncate pl-4 text-left text-sm font-medium lg:pl-4 sm:w-4/5'>
                        {user.email}
                      </p>
                      <p className='min-w-[86px] truncate pl-2 text-left text-sm sm:w-1/5'>
                        {user.display_name}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {inviteUsers.length > 0 && (
            <div className='mt-[14px] max-h-[280px] w-full overflow-y-auto border border-solid border-gray-200 bg-white py-2 lg:mt-[18px] lg:max-h-[412px]'>
              {inviteUsers?.map((user, index: number) => (
                <div
                  key={index}
                  className='flex hover:bg-gray-10'
                >
                  <div className='flex w-full items-center justify-between py-2 pl-4 pr-8 lg:px-2 lg:pr-6'>
                    <div className='flex min-w-0 flex-1 items-center'>
                      <div className='shrink-0'>
                        <img
                          width={28}
                          height={28}
                          src={user.avatar_url || DefaultIcon}
                          alt=''
                          className='size-7 rounded-full object-contain'
                        />
                      </div>
                      <p className='truncate pl-4 text-sm font-medium lg:pl-2 sm:w-4/5'>
                        {user.email}
                      </p>
                      <p className='min-w-[60px] truncate pl-2 text-sm sm:w-4/5'>{user.display_name}</p>
                    </div>
                    <button
                      type='button'
                      className='shrink-0 text-red-500'
                      onClick={() => handleMemberRemoveClick(index)}
                    >
                      <img
                        width={24}
                        height={24}
                        src={TrashIcon}
                        alt=''
                        className='size-6 object-contain'
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className='mt-7 flex items-center justify-center gap-8 lg:mt-10 lg:gap-2'>
            <div className='w-[172px] md:w-full'>
              <Button
                size='m'
                variant='secondary'
                fullRounded
                fullWidth
                outline
                onClick={handleCancelClick}
              >
                キャンセル
              </Button>
            </div>
            <div className='w-[172px] md:w-full'>
              <Button
                size='m'
                variant={inviteUsers ? 'primary' : 'tertiary'}
                outline={inviteUsers.length > 0}
                disabled={inviteUsers.length === 0 || isLoadingInvitationClick}
                fullRounded
                fullWidth
                onClick={handleInvitationClick}
                loading={isLoadingInvitationClick}
              >
                招待する
              </Button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmMemberInvitationLeaveModal
        isOpen={isModalOpen}
        onConfirm={confirmReturn}
        onClose={cancelReturn}
      />
      <ConfirmMemberInvitationLeaveModal
        isOpen={isLeaveModalOpen}
        onConfirm={onConfirm}
        onClose={onCancel}
      />
    </main>
  );
}

export default CommunityMemberInvitationForm;
